import type { ReactNode } from 'react'
import React from 'react'
import { useLocalStorage } from 'react-use'
import { Card, Collapse } from 'react-bootstrap'
import { HorizontalStack } from '../HorizontalStack'
import { CollapseControl } from '../CollapseControl'

export type CollapsibleCardProps = {
  children: ReactNode
  title: ReactNode
  // Rendered under the title, which turns bold once a description is present.
  description?: ReactNode
  initialShow?: boolean
  toggleStateKey?: string
  headerToggle?: boolean
}
export const CollapsibleCard = ({
  children,
  title,
  description,
  initialShow = true,
  toggleStateKey = 'collapsibleCard',
  headerToggle = false,
}: CollapsibleCardProps) => {
  const [show, setShow] = useLocalStorage<boolean>(toggleStateKey, initialShow)
  const toggle = () => setShow(!show)

  // Clicks on interactive header content (links, buttons, inputs) must not flip the card —
  // only the plain header surface toggles. The chevron keeps its own toggle via CollapseControl.
  const stopInteractiveToggle = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest?.('a,button,input,select,textarea,[role="button"]')) {
      e.stopPropagation()
    }
  }

  return (
    <Card>
      {/* The whole-bar toggle is opt-in so every consumer keeps chevron-only behavior unless
          it passes headerToggle. The chevron stops propagation so its own toggle fires once. */}
      <Card.Header onClick={headerToggle ? toggle : undefined} style={headerToggle ? { cursor: 'pointer' } : undefined}>
        <HorizontalStack className="justify-content-between">
          <div onClick={headerToggle ? stopInteractiveToggle : undefined}>
            {description ? (
              <>
                <b>{title}</b>
                <div>{description}</div>
              </>
            ) : (
              title
            )}
          </div>
          <div onClick={headerToggle ? (e) => e.stopPropagation() : undefined}>
            <CollapseControl show={show} setShow={toggle} />
          </div>
        </HorizontalStack>
      </Card.Header>
      <Collapse in={show}>
        {/*We need to ise the hide-if here so that it will not destroy the components on rerender*/}
        <div>
          <Card.Body>{children}</Card.Body>
        </div>
      </Collapse>
    </Card>
  )
}
